'use client'
import { type ChangeEvent, type FC, useState } from 'react'
import type { FormData } from '../types'
import styles from './BuildingDetails.module.scss'
import { useLocalisation } from '@/tools/i18n/client'
import {
  EditIcon,
  HouseIcon,
  IconButton,
  IconButtonSize,
  IconButtonVariants,
  PlusIcon,
  TextField,
} from '@/tools/ui'
import { ListItem } from '@/tools/ui'
import { useAddressBookContext } from '@/user/address-book/AddressBookContext'
import type {
  UserAddress,
  UserAddressFieldType,
  UserAddressNew,
} from '@/user/address-book/entities'
import { BUILDING_DETAILS_T_KEYS } from '@/user/address-book/ui/constants'

interface Props {
  onFieldChange: (fieldType: UserAddressFieldType, value: string) => void
  address: UserAddress | UserAddressNew
  fields: FormData['fields']
  buildingDetailsRef: React.Ref<HTMLDivElement>
}

export const BuildingDetails: FC<Props> = ({
  onFieldChange: handleFieldChange,
  address,
  fields,
  buildingDetailsRef,
}) => {
  const { t } = useLocalisation()
  const { navigate, isMobile } = useAddressBookContext()

  const hasAdditionalInfo = Boolean(
    fields.find((field) => field.type === 'ADDITIONAL_INFORMATION')?.value
  )

  const cityCountryString = address.cityName
    ? `${address.cityName}, ${address.countryCode}`
    : address.countryCode

  const [isAdditionalInfoCollapsed, setIsAdditionalInfoCollapsed] =
    useState<boolean>(!hasAdditionalInfo)

  const handleBuildingDetailsChange = (fieldType: UserAddressFieldType, value: string) => {
    handleFieldChange(fieldType, value.trim())
  }

  const handleEditAddressLineClick = () => {
    navigate('onEditAddressLine')
  }

  const handleCollapseInputClick = () => {
    setIsAdditionalInfoCollapsed(false)
  }

  return (
    <div ref={buildingDetailsRef} className={styles.buildingDetails} data-testid="building-details">
      {isMobile && <h3 className={styles.title}>{t('address-details.building-details.title')}</h3>}
      <ListItem
        leadingContent={<HouseIcon outline={true} />}
        trailingContent={
          <IconButton
            aria-label={t('common.edit')}
            icon={EditIcon}
            size={IconButtonSize.small}
            variant={IconButtonVariants.neutral}
            onClick={handleEditAddressLineClick}
          />
        }
      >
        <div className={styles.addressInfoBlock}>
          <div>
            <p className={styles.street}>{address.addressLine}</p>
            <p className={styles.city}>{cityCountryString}</p>
          </div>
        </div>
      </ListItem>

      <div className={styles.fieldsContainer}>
        {fields.map((field) => {
          if (field.type === 'ADDITIONAL_INFORMATION' && isAdditionalInfoCollapsed) {
            return (
              <div
                key={field.type}
                className={styles.collapsedInput}
                onClick={handleCollapseInputClick}
              >
                <PlusIcon className={styles.plusIcon} />
                <p>{t(BUILDING_DETAILS_T_KEYS[field.type])}</p>
              </div>
            )
          } else {
            return (
              <TextField
                key={field.type}
                aria-label={t(BUILDING_DETAILS_T_KEYS[field.type])}
                error={field.error}
                label={t(BUILDING_DETAILS_T_KEYS[field.type])}
                secondaryLabel={field.required ? `(${t('common.required')})` : ''}
                value={field.value}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleBuildingDetailsChange(field.type, e.target.value)
                }
              />
            )
          }
        })}
      </div>
    </div>
  )
}
